import { useEffect, useState } from "react";
import { Tag, Loader2, RefreshCw, Plus, Pencil, BarChart3, X, Power } from "lucide-react";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import PromoFormModal from "@/components/promo/PromoFormModal";
import {
  formatRupiah, discountLabel, formatPromoDate,
  PROMO_STATUS_LABELS, PROMO_STATUS_STYLES,
} from "@/components/promo/promoMeta";

export default function OwnerPromosPage() {
  const [promos, setPromos] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [usagePromo, setUsagePromo] = useState(null);

  const load = () => {
    setLoading(true);
    setError(false);
    Promise.all([
      apiClient.get("/owner/promos"),
      apiClient.get("/properties"),
    ])
      .then(([p, props]) => {
        setPromos(p.data || []);
        setProperties(props.data || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleStatus = async (promo) => {
    const next = promo.status === "active" ? "inactive" : "active";
    try {
      await apiClient.patch(`/owner/promos/${promo.id}/status`, { status: next });
      toast.success(next === "active" ? "Promo diaktifkan" : "Promo dinonaktifkan");
      load();
    } catch {
      toast.error("Gagal mengubah status promo");
    }
  };

  return (
    <OwnerLayout testid="owner-promos-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Promo &amp; Diskon</h1>
          <p className="mt-2 text-sm text-muted-foreground">Buat promo untuk property Anda. Diskon hanya mengurangi harga kamar, tidak termasuk deposit.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="owner-promos-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
            <RefreshCw size={14} /> Muat Ulang
          </button>
          <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="owner-promo-create-btn"
            disabled={properties.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-[1.03] active:scale-95 transition-transform disabled:opacity-50">
            <Plus size={15} /> Buat Promo
          </button>
        </div>
      </div>

      {properties.length === 0 && !loading && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Anda belum memiliki property. Tambahkan property terlebih dahulu untuk membuat promo.
        </div>
      )}

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="owner-promos-error">
          <p className="text-sm text-destructive">Gagal memuat promo.</p>
          <button onClick={load} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : promos.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="owner-promos-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><Tag size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada promo.</h3>
          <p className="mt-2 text-sm text-muted-foreground">Buat promo pertama Anda untuk menarik lebih banyak penyewa.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="owner-promos-list">
          {promos.map((promo, i) => (
            <div key={promo.id} data-testid={`owner-promo-${i}`} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-base font-semibold truncate">{promo.promo_name}</h3>
                    <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20" data-testid={`owner-promo-code-${i}`}>{promo.promo_code}</span>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${PROMO_STATUS_STYLES[promo.status] || ""}`}>{PROMO_STATUS_LABELS[promo.status] || promo.status}</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{promo.property_name || "Property"}</p>
                </div>
                <div className="text-right">
                  <p className="font-heading text-xl font-bold text-primary" data-testid={`owner-promo-discount-${i}`}>{discountLabel(promo)}</p>
                  <p className="text-xs text-muted-foreground">Diskon harga kamar</p>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs text-muted-foreground">
                <Info label="Periode" value={`${formatPromoDate(promo.start_date)} – ${formatPromoDate(promo.end_date)}`} />
                <Info label="Min. Booking" value={promo.minimum_booking_amount ? formatRupiah(promo.minimum_booking_amount) : "-"} />
                <Info label="Batas Total" value={promo.usage_limit != null ? `${promo.usage_count}/${promo.usage_limit}` : `Dipakai ${promo.usage_count}× · tak terbatas`} />
                <Info label="Batas / Customer" value={promo.usage_limit_per_customer != null ? String(promo.usage_limit_per_customer) : "-"} />
              </div>
              {promo.discount_type === "percentage" && promo.max_discount_amount != null && (
                <p className="mt-2 text-xs text-muted-foreground">Maks. potongan: {formatRupiah(promo.max_discount_amount)}</p>
              )}

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button onClick={() => { setEditing(promo); setFormOpen(true); }} data-testid={`owner-promo-edit-${i}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:bg-secondary/60 transition-colors">
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => toggleStatus(promo)} data-testid={`owner-promo-toggle-${i}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:bg-secondary/60 transition-colors">
                  <Power size={13} /> {promo.status === "active" ? "Nonaktifkan" : "Aktifkan"}
                </button>
                <button onClick={() => setUsagePromo(promo)} data-testid={`owner-promo-usage-${i}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:bg-secondary/60 transition-colors">
                  <BarChart3 size={13} /> Lihat Usage
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <PromoFormModal open={formOpen} promo={editing} properties={properties}
        onClose={() => setFormOpen(false)} onSaved={load} />
      {usagePromo && <UsageModal promo={usagePromo} onClose={() => setUsagePromo(null)} />}
    </OwnerLayout>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="uppercase tracking-wide text-[10px] font-bold">{label}</p>
      <p className="text-foreground font-medium">{value}</p>
    </div>
  );
}

function UsageModal({ promo, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/owner/promos/${promo.id}/usages`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [promo.id]);

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="promo-usage-modal">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-2xl">
          <div>
            <h3 className="font-heading text-lg font-semibold">Usage — {promo.promo_code}</h3>
            <p className="text-xs text-muted-foreground">Total dipakai: {promo.usage_count}</p>
          </div>
          <button onClick={onClose} data-testid="promo-usage-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-primary" size={24} /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="promo-usage-empty">Belum ada pemakaian promo ini.</p>
          ) : (
            <div className="space-y-3" data-testid="promo-usage-list">
              {rows.map((u) => (
                <div key={u.id} className="rounded-xl border border-border p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{u.customer_name || "Customer"}</span>
                    <span className="font-semibold text-primary">-{formatRupiah(u.discount_amount)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{formatPromoDate(u.used_at)} · Booking {u.booking_id.slice(0, 8)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
